import { useI18n } from '../../i18n';
import type { OnlinePortalUser } from '../../lib/adminPresenceApi';

function activityAge(user: OnlinePortalUser): number | null {
  return typeof user.lastActionSecondsAgo === 'number' && Number.isFinite(user.lastActionSecondsAgo)
    && user.lastActionSecondsAgo >= 0 && user.lastActionAt && Number.isFinite(Date.parse(user.lastActionAt))
    ? user.lastActionSecondsAgo : null;
}

export default function OnlineUsersPanel({ users, error }: { users: OnlinePortalUser[] | null; error: boolean }) {
  const { t, locale } = useI18n();
  const active = users?.filter(user => { const age = activityAge(user); return age !== null && age < 120; }).length ?? 0;
  const unknown = users?.filter(user => activityAge(user) === null).length ?? 0;
  return <>
    <div className="mb-2 text-xs font-semibold text-gray-900">{t('sidebar_online_users_title')}</div>
    {error ? <p role="alert" className="text-xs text-red-600">{t('sidebar_online_users_error')}</p>
      : users === null ? <p className="text-xs text-gray-500">{t('loading')}</p>
        : users.length === 0 ? <p className="text-xs text-gray-500">{t('sidebar_online_users_empty')}</p>
          : <>
            <p className="mb-2 border-b border-gray-200 pb-2 text-xs text-gray-700">{t('sidebar_activity_summary', { active, unknown })}</p>
            <div className="max-h-64 divide-y divide-gray-100 overflow-y-auto">
              {users.map(user => {
                const age = activityAge(user);
                const recent = age !== null && age < 120;
                const ago = age === null ? '' : age < 60 ? t('sidebar_activity_just_now')
                  : age < 3600 ? t('sidebar_activity_minutes_ago', { count: Math.floor(age / 60) })
                    : t('sidebar_activity_hours_ago', { count: Math.floor(age / 3600) });
                return <div key={user.email || user.displayName} className="py-2 first:pt-0 last:pb-0">
                  <div className="break-words text-xs font-medium text-gray-900">{user.displayName}</div>
                  {user.email && <div className="break-all text-[11px] text-gray-500">{user.email}</div>}
                  <div className={`mt-1 text-[11px] font-medium ${recent ? 'text-primary-700' : 'text-gray-600'}`}>
                    {t(age === null ? 'sidebar_activity_unknown' : recent ? 'sidebar_activity_recent' : 'sidebar_activity_no_recent')}
                  </div>
                  {age !== null && user.lastActionAt && <p className="text-[11px] text-gray-500">
                    {t('sidebar_activity_last_action')} <time dateTime={user.lastActionAt} title={new Date(user.lastActionAt).toLocaleString(locale)}>
                      {new Date(user.lastActionAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </time> · {ago}
                  </p>}
                  {user.connections > 1 && <p className="mt-0.5 text-[10px] text-gray-500">{t('sidebar_online_users_connections', { count: user.connections })}</p>}
                </div>;
              })}
            </div>
          </>}
    <p className="mt-3 border-t border-gray-200 pt-2 text-[11px] text-gray-500">{t('sidebar_activity_help')}</p>
  </>;
}
